import axios from 'axios';

import { getApiBaseUrl } from './runtimeUrls';

export const BACKEND_URL = getApiBaseUrl();
export const ADMIN_STRATEGY_OPS_BACKTEST_RUN_PATH = '/api/admin/strategy-ops/backtests/run';
const ADMIN_BACKTEST_LEGACY_RUN_PATH = '/api/admin/backtests/run';

export const api = axios.create({
  baseURL: BACKEND_URL,
  withCredentials: true,
});

function devLog(message: string, details?: Record<string, unknown>) {
  if (!import.meta.env.DEV) return;
  console.info(message, details || {});
}

export type AdminUser = {
  id: number;
  email: string;
  fullName: string;
  role: 'user' | 'admin' | 'superadmin' | string;
  isActive: boolean;
};

export type MacroEvent = {
  id: string;
  title: string;
  eventType: string;
  currency: string;
  impactLevel: 'high' | 'medium' | 'low' | string;
  eventTime: string;
  sourceType: string;
  sourceName: string;
  sourceReference?: string | null;
  notes?: string | null;
  riskSummary?: string | null;
  status?: 'draft' | 'confirmed' | 'suggested' | 'rejected' | 'cancelled' | string;
  aiConfidence?: number | null;
  aiNotes?: string | null;
  needsReview?: boolean;
  inferredEventTime?: boolean;
  dedupeKey?: string | null;
  approvedBy?: string | null;
  approvedAt?: string | null;
  lastSeenAt?: string | null;
  liveUsage?: 'confirmed_blocking' | 'warning_only' | 'soft_penalty' | string;
  isActive: boolean;
};

export type MacroAIDraft = {
  title: string;
  eventType: 'cpi' | 'nfp' | 'fomc' | 'interest_rate' | 'gdp' | 'retail_sales' | 'other';
  currency: string;
  impactLevel: 'high' | 'medium' | 'low';
  eventTime: string | null;
  notes: string | null;
  riskSummary: string | null;
  sourceType: 'ai_assisted';
  sourceName: 'openai';
  sourceReference: string | null;
  aiConfidence: number | null;
  aiNotes: string | null;
  needsReview: true;
  inferredEventTime: boolean;
  isActive: boolean;
  status: 'draft' | 'suggested';
};

export async function loginAdmin(email: string, password: string) {
  devLog('[admin-auth] attempting login', { email });
  await api.post('/api/auth/login', { email, password });
  devLog('[admin-auth] login endpoint returned success, requesting /api/auth/me');
  const me = await api.get<{ user: AdminUser }>('/api/auth/me');
  devLog('[admin-auth] /api/auth/me succeeded after login', { role: me.data.user?.role });
  return me.data.user;
}

export async function getMe() {
  devLog('[admin-auth] checking current session via /api/auth/me');
  const res = await api.get<{ user: AdminUser }>('/api/auth/me');
  devLog('[admin-auth] session check succeeded', { role: res.data.user?.role });
  return res.data.user;
}

export async function logout() {
  devLog('[admin-auth] logging out');
  await api.post('/api/auth/logout');
  devLog('[admin-auth] logout request completed');
}

export type AdminChartAnalysisReview = {
  setup_validity: boolean;
  direction: 'BUY' | 'SELL' | 'WAIT';
  entry_zone: number | null;
  stop_loss: number | null;
  take_profit_levels: number[];
  risk_reward: number | null;
  confidence: number | null;
  invalidation_reason: string | null;
  explanation: string | null;
  safe_to_forward: boolean;
  blocked_reason: string | null;
  timeframe: string | null;
  strategy_context: string | null;
  risk_note: string | null;
  expires_at: string | null;
};

export async function adminUploadChart(form: FormData) {
  devLog('[admin-chart-analysis] upload request', { url: '/api/admin/chart-analysis/upload' });
  const res = await api.post('/api/admin/chart-analysis/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  devLog('[admin-chart-analysis] upload response', { id: res.data?.id, status: res.data?.status, symbol: res.data?.symbol, requestedActionMode: res.data?.requested_action_mode });
  return res.data;
}

export async function adminAnalyzeChart(id: number, payload: Record<string, unknown> = {}) {
  devLog('[admin-chart-analysis] analyze request', { url: `/api/admin/chart-analysis/${id}/analyze`, payload, id });
  const res = await api.post(`/api/admin/chart-analysis/${id}/analyze`, payload);
  return res.data;
}

export async function listAdminChartAnalyses() {
  const res = await api.get('/api/admin/chart-analysis');
  return res.data;
}

export async function forwardAdminChartAnalysis(id: number, payload: Record<string, unknown>) {
  const res = await api.post(`/api/admin/chart-analysis/${id}/forward`, payload);
  return res.data;
}

export async function listAdminTradeRecommendations() {
  const res = await api.get('/api/admin/trade-recommendations');
  return res.data;
}

export async function cancelAdminTradeRecommendation(id: number, reason?: string) {
  const res = await api.post(`/api/admin/trade-recommendations/${id}/cancel`, { reason: reason || null });
  return res.data;
}

export type BacktestStrategy = { key: string; name: string; category?: string; status?: string; latestMetrics?: Record<string, unknown> | null; lastBacktestAt?: string | null; isBacktestable?: boolean };
export async function listBacktestStrategies() { return (await api.get<{items: BacktestStrategy[]}>('/api/admin/backtests/strategies')).data; }
export async function createBacktestJob(payload: Record<string, unknown>) {
  console.error('[ADMIN_BACKTEST_LEGACY_ENDPOINT_USED]', {
    endpoint: ADMIN_BACKTEST_LEGACY_RUN_PATH,
    replacement: ADMIN_STRATEGY_OPS_BACKTEST_RUN_PATH,
    caller: 'Backtests.tsx/lib_api.ts',
  });
  return (await api.post(ADMIN_STRATEGY_OPS_BACKTEST_RUN_PATH, payload)).data;
}
export async function getBacktestJob(jobId: string) { return (await api.get(`/api/admin/backtests/jobs/${jobId}`)).data; }
export async function listBacktestReports() { return (await api.get('/api/admin/backtests/reports')).data; }
export async function getBacktestReport(jobId: string) { return (await api.get(`/api/admin/backtests/reports/${jobId}`)).data; }

export async function listStrategyOpsClientVisibleStrategies() { return (await api.get('/api/admin/strategy-ops/client-visible-strategies')).data; }
export async function runStrategyOpsBacktest(payload: Record<string, unknown>) { return (await api.post(ADMIN_STRATEGY_OPS_BACKTEST_RUN_PATH, payload)).data; }
export async function getStrategyOpsBacktestJob(jobId: string) { return (await api.get(`/api/admin/strategy-ops/backtests/jobs/${jobId}`)).data; }
export async function listStrategyOpsBacktestReports() { return (await api.get('/api/admin/strategy-ops/backtests/reports')).data; }
export async function getStrategyOpsCatalogDiagnostics() { return (await api.get('/api/admin/strategy-ops/catalog-diagnostics')).data; }
